import type { CheckoutPhase } from './types';

/** Personal Zippy handles: 3–20 chars, optional single dot, no leading/trailing dot. */
export const PAYMENT_ID_PATTERN = /^(?=.{3,20}$)[a-z0-9]+(?:\.[a-z0-9]+)?$/;

export const E164_PHONE_PATTERN = /^\+[1-9]\d{7,14}$/;

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function normalizePaymentId(value: string): string {
  return value.trim().toLowerCase();
}

/** Normalize a payer verify query before sending to the API. */
export function normalizePayerQuery(value: string): string {
  const trimmed = value.trim();
  if (trimmed.includes('@')) {
    return trimmed.toLowerCase();
  }
  if (trimmed.startsWith('+')) {
    return trimmed;
  }
  return trimmed.toLowerCase();
}

export function isValidPaymentId(value: string): boolean {
  return PAYMENT_ID_PATTERN.test(normalizePaymentId(value));
}

/** Client-side pre-validation for payer verify `query` (server is authoritative). */
export function isValidPayerQuery(value: string): boolean {
  const query = value.trim();
  if (query.length < 3 || query.length > 100 || /\s/.test(query)) {
    return false;
  }
  if (/[\x00-\x1f\x7f]/.test(query)) {
    return false;
  }
  if (query.includes('@')) {
    return EMAIL_PATTERN.test(query);
  }
  if (query.startsWith('+')) {
    return E164_PHONE_PATTERN.test(query);
  }
  return PAYMENT_ID_PATTERN.test(query.toLowerCase());
}

export function paymentIdValidationMessage(): string {
  return 'Enter 3–20 lowercase letters or digits, with at most one dot (e.g. john.doe12).';
}

export function payerQueryValidationMessage(): string {
  return 'Enter a Zippy ID, email, or phone number in E.164 format (e.g. +15551234567).';
}

export function isUuidV4(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  );
}

/** Locale per currency so amounts use the standard symbol (e.g. USD → $1.00, not US$1.00). */
const CURRENCY_LOCALES: Partial<Record<string, string>> = {
  USD: 'en-US',
};

export function formatAmount(amount: string, currency: string): string {
  const parsed = Number.parseFloat(amount);
  if (Number.isNaN(parsed)) {
    return `${amount} ${currency}`;
  }
  const locale = CURRENCY_LOCALES[currency] ?? 'en-US';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(parsed);
}

export const TERMINAL_EXIT_COUNTDOWN_SEC = 7;

export type TerminalExitMode = 'redirect' | 'close';

export function terminalExitLeadText(
  exitMode: TerminalExitMode,
  embedMode: 'inline' | 'modal',
  outcome: 'success' | 'failure' = 'success',
): string {
  if (exitMode === 'redirect') {
    return outcome === 'failure'
      ? 'You will be redirected shortly in'
      : 'You will be redirected in';
  }
  if (embedMode === 'modal') {
    return 'This window will close automatically in';
  }
  return 'This checkout will close automatically in';
}

export function outcomeTerminalCopy(
  phase: CheckoutPhase,
): { kind: 'success' | 'failure'; title: string; detail: string } {
  switch (phase) {
    case 'success':
      return {
        kind: 'success',
        title: 'Payment complete',
        detail: 'Your payment was successful.',
      };
    case 'expired':
      return {
        kind: 'failure',
        title: 'Payment timed out',
        detail:
          'The time to complete this payment has ended. Return to the merchant and try again.',
      };
    case 'failed':
      return {
        kind: 'failure',
        title: 'Payment failed',
        detail: 'Your payment could not be completed. Please try again or contact the merchant.',
      };
    case 'cancelled':
      return {
        kind: 'failure',
        title: 'Payment cancelled',
        detail: 'This payment was cancelled.',
      };
    case 'declined':
      return {
        kind: 'failure',
        title: 'Payment declined',
        detail: 'Your payment was declined. Please try again or contact the merchant.',
      };
    case 'invalid-session':
      return {
        kind: 'failure',
        title: 'Checkout unavailable',
        detail:
          'This checkout link is no longer valid. Please ask the merchant for a new one.',
      };
    default:
      return {
        kind: 'failure',
        title: 'Something went wrong',
        detail: 'Please try again or contact the merchant.',
      };
  }
}

export function formatCountdown(ms: number): string {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;

  if (min >= 60) {
    const hr = Math.floor(min / 60);
    const remMin = min % 60;
    return remMin > 0 ? `${hr}h ${remMin}m` : `${hr}h`;
  }

  if (min > 0) {
    return `${min}m ${sec.toString().padStart(2, '0')}s`;
  }

  return `${sec}s`;
}

export function formatCountdownAccessible(ms: number): string {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;

  if (min >= 60) {
    const hr = Math.floor(min / 60);
    const remMin = min % 60;
    const parts = [`${hr} hour${hr === 1 ? '' : 's'}`];
    if (remMin > 0) {
      parts.push(`${remMin} minute${remMin === 1 ? '' : 's'}`);
    }
    return parts.join(' ');
  }

  if (min > 0) {
    if (sec > 0) {
      return `${min} minute${min === 1 ? '' : 's'} ${sec} second${sec === 1 ? '' : 's'}`;
    }
    return `${min} minute${min === 1 ? '' : 's'}`;
  }

  return `${sec} second${sec === 1 ? '' : 's'}`;
}

/** Up to two initials from a display name for avatar fallbacks. */
export function formatDisplayInitials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

export function formatIdleDeadline(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function resolveContainer(container: HTMLElement | string): HTMLElement {
  if (typeof container !== 'string') {
    return container;
  }
  const element = document.querySelector<HTMLElement>(container);
  if (!element) {
    throw new Error(`Zippy Pay: container not found: ${container}`);
  }
  return element;
}

export function normalizeBaseUrl(baseUrl: string): string {
  return baseUrl.replace(/\/+$/, '');
}

/** Appends checkout redirect query params, preserving any existing search string. */
export function appendRedirectQueryParams(
  url: string,
  params: Record<string, string | null | undefined>,
): string {
  const isAbsolute = /^https?:\/\//i.test(url);
  const base = typeof window !== 'undefined' ? window.location.href : 'https://localhost/';
  const parsed = new URL(url, base);

  for (const [key, value] of Object.entries(params)) {
    if (value != null && value !== '') {
      parsed.searchParams.set(key, value);
    }
  }

  if (isAbsolute) {
    return parsed.toString();
  }

  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}
